import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { SurfaceCard } from '../../../shared/ui';
import { lightTheme as theme } from '../../../shared/theme';
import { formatarData } from '../../../shared/utils/dateFormatters';

const Card = styled(SurfaceCard)`
    width: 100%;
`;

const CardTitle = styled.h3`
    margin: 0 0 14px;
    font-size: 16px;
    font-weight: 700;
    color: ${theme.colors.primary};
`;

const CardContent = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: 20px;
    row-gap: 16px;
    align-items: start;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
`;

const InfoItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;

    .label {
        color: ${theme.colors.textMuted};
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.03em;
    }

    .value {
        color: ${theme.colors.text};
        font-size: 14px;
        font-weight: 500;
        line-height: 1.4;
    }
`;

const QrColumn = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: center;
    justify-self: end;
    gap: 8px;

    @media (max-width: 768px) {
        align-self: start;
        justify-self: start;
    }
`;

const QrLabel = styled.span`
    color: ${theme.colors.textSecondary};
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
`;

const QrBox = styled.div`
    width: 140px;
    height: 140px;
    border-radius: 8px;
    border: 1px solid ${theme.colors.gray200};
    background: ${theme.colors.text};
    color: ${theme.colors.paper};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.05em;
`;

const QrImage = styled.img`
    width: 140px;
    height: 140px;
    object-fit: contain;
    border-radius: 8px;
    border: 1px solid ${theme.colors.gray200};
    background: ${theme.colors.paper};
`;

const EmptyValue = '--';

function EquipmentInfoSection({ equipment, labels }) {
    const nextReviewText = formatarData(equipment?.proxima_revisao);
    const qrCodeImage =
        equipment?.qr_code_image ||
        equipment?.qrcode_image ||
        equipment?.qr_code_url ||
        equipment?.qrcode_url ||
        null;
    const qrCodeText =
        equipment?.qr_code ||
        equipment?.qrcode ||
        'QRCODE';

    return (
        <Card padding={20}>
            <CardTitle>{labels.dataSectionTitle}</CardTitle>

            <CardContent>
                <InfoGrid>
                    <InfoItem>
                        <span className='label'>{labels.codeLabel}</span>
                        <span className='value'>{equipment?.id || EmptyValue}</span>
                    </InfoItem>
                    <InfoItem>
                        <span className='label'>Categoria</span>
                        <span className='value'>{equipment?.categoria_nome || EmptyValue}</span>
                    </InfoItem>
                    <InfoItem>
                        <span className='label'>Produto</span>
                        <span className='value'>{equipment?.produto_nome || EmptyValue}</span>
                    </InfoItem>
                    <InfoItem>
                        <span className='label'>Serviço</span>
                        <span className='value'>{equipment?.servico_nome || equipment?.nome || EmptyValue}</span>
                    </InfoItem>
                    <InfoItem>
                        <span className='label'>{labels.brandLabel}</span>
                        <span className='value'>{equipment?.marca || EmptyValue}</span>
                    </InfoItem>
                    <InfoItem>
                        <span className='label'>{labels.modelLabel}</span>
                        <span className='value'>{equipment?.modelo || EmptyValue}</span>
                    </InfoItem>
                    <InfoItem>
                        <span className='label'>{labels.caLabel}</span>
                        <span className='value'>{equipment?.ca || EmptyValue}</span>
                    </InfoItem>
                    <InfoItem>
                        <span className='label'>{labels.serialLabel}</span>
                        <span className='value'>{equipment?.serie || EmptyValue}</span>
                    </InfoItem>
                    <InfoItem>
                        <span className='label'>{labels.lastRevisionLabel}</span>
                        <span className='value'>{formatarData(equipment?.ultima_revisao)}</span>
                    </InfoItem>
                    <InfoItem>
                        <span className='label'>{labels.nextRevisionLabel}</span>
                        <span className='value'>{nextReviewText || EmptyValue}</span>
                    </InfoItem>
                </InfoGrid>

                <QrColumn>
                    <QrLabel>{labels.qrCodeLabel}</QrLabel>
                    {qrCodeImage ? (
                        <QrImage src={qrCodeImage} alt={labels.qrCodeLabel} />
                    ) : (
                        <QrBox>{qrCodeText}</QrBox>
                    )}
                </QrColumn>
            </CardContent>
        </Card>
    );
}

EquipmentInfoSection.propTypes = {
    equipment: PropTypes.object,
    labels: PropTypes.shape({
        dataSectionTitle: PropTypes.string.isRequired,
        codeLabel: PropTypes.string.isRequired,
        itemLabel: PropTypes.string.isRequired,
        brandLabel: PropTypes.string.isRequired,
        modelLabel: PropTypes.string.isRequired,
        caLabel: PropTypes.string.isRequired,
        serialLabel: PropTypes.string.isRequired,
        lastRevisionLabel: PropTypes.string.isRequired,
        nextRevisionLabel: PropTypes.string.isRequired,
        qrCodeLabel: PropTypes.string.isRequired,
    }).isRequired,
};

EquipmentInfoSection.defaultProps = {
    equipment: null,
};

export default EquipmentInfoSection;
